import React, { useCallback, useState } from 'react';
import { isFunction } from 'lodash';
import PropTypes from 'prop-types';
import scaleSize from 'utils/scale-size';
import PLAYER_TYPES from 'constants/player-types';
import {
  StyledProgressWrapper,
  StyledCurrentPosition,
  StyledProgressFull,
  StyledProgressElapsed,
  StyledLiveProgressElapsed,
  StyledTimeLabel,
  StyledHoveredPosition,
} from '../StyledComponents';

const PROGRESS_BAR_SECTION_NAME = 'PROGRESS_BAR';

const PROGRESS_WIDTH = scaleSize(1560);

const calculateProgress = (currentTime, duration, seekPending) => {
  const value = duration ? (PROGRESS_WIDTH / duration) * (seekPending ?? currentTime) : 0;
  return Number(value.toFixed(2));
};

const formatTime = (seconds = 0) => {
  if (!seconds) return '00:00:00';
  return new Date(seconds * 1000).toISOString().substr(11, 8);
};

const ProgressBar = ({ playerType, seekPending, currentTime, currentLiveTime, duration, onClick }) => {
  const [isHoverProgressVisible, setIsHoverProgressVisible] = useState(false);
  const [hoverTimePosition, setHoverTimePosition] = useState(0);
  const progress = calculateProgress(currentTime, duration, seekPending);
  const hoverProgress = calculateProgress(currentTime, duration, hoverTimePosition);
  const liveProgress = calculateProgress(currentLiveTime, duration);

  const onMouseMove = useCallback(
    ({ clientX }) => {
      const leftMargin = scaleSize(137);
      const currOffsetX = clientX - leftMargin;
      const percents = (currOffsetX / PROGRESS_WIDTH) * 100;
      if (percents > 100 || percents < 0) return;
      setHoverTimePosition((duration / 100) * percents);
    },
    [duration],
  );

  const onMouseEnter = useCallback(() => setIsHoverProgressVisible(true), []);
  const onMouseLeave = useCallback(() => setIsHoverProgressVisible(false), []);

  return (
    <>
      <StyledProgressWrapper
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => isFunction(onClick) && onClick(hoverTimePosition)}
        sectionName={PROGRESS_BAR_SECTION_NAME}
        config={{
          defaultElement: '.current-position',
          leaveFor: {
            right: '',
            left: '',
            up: playerType === PLAYER_TYPES.TIMESHIFT ? '' : undefined,
            down: '@PLAYER_CONTROLS',
          },
        }}
        width={PROGRESS_WIDTH}
      >
        <StyledCurrentPosition className="current-position" progress={progress} isSeeking={seekPending !== null}>
          <span>{formatTime(seekPending || 0)}</span>
        </StyledCurrentPosition>
        {isHoverProgressVisible && (
          <StyledHoveredPosition progress={hoverProgress} isSeeking={isHoverProgressVisible}>
            <span>{formatTime(hoverTimePosition || 0)}</span>
          </StyledHoveredPosition>
        )}
        <StyledProgressFull width={PROGRESS_WIDTH}>
          {liveProgress && <StyledLiveProgressElapsed progress={liveProgress} />}
          <StyledProgressElapsed progress={progress} />
        </StyledProgressFull>
      </StyledProgressWrapper>
      <StyledTimeLabel isSeeking={seekPending !== null}>
        {formatTime(currentTime)} / <span>{formatTime(duration)}</span>
      </StyledTimeLabel>
    </>
  );
};

ProgressBar.propTypes = {
  progress: PropTypes.number,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  seekPending: PropTypes.number,
};

export default ProgressBar;
